import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FaIcons from 'react-icons/fa';

const I = (Icon: any) => Icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FaRobot = I(FaIcons.FaRobot);
const FaPlane = I(FaIcons.FaPlane);
const FaBrain = I(FaIcons.FaBrain);
const FaDrone = I(FaIcons.FaHelicopter);
const FaTrophy = I(FaIcons.FaTrophy);
const FaGamepad = I(FaIcons.FaGamepad);
const FaBullseye = I(FaIcons.FaBullseye);
const FaArrowRight = I(FaIcons.FaArrowRight);
const FaMapMarkerAlt = I(FaIcons.FaMapMarkerAlt);
const FaCalendarAlt = I(FaIcons.FaCalendarAlt);
const FaClock = I(FaIcons.FaClock);
const FaUsers = I(FaIcons.FaUsers);
const FaPhone = I(FaIcons.FaPhone);
const FaEnvelope = I(FaIcons.FaEnvelope);
const FaInstagram = I(FaIcons.FaInstagram);
const FaLinkedin = I(FaIcons.FaLinkedin);
const FaFacebook = I(FaIcons.FaFacebookF);
const FaRocket = I(FaIcons.FaRocket);
const FaMicrochip = I(FaIcons.FaMicrochip);

const navy = '#0a2c6e';
const navyDeep = '#061d4a';
const orange = '#f97316';
const orangeDark = '#ea580c';
const ink = '#0f172a';
const muted = '#64748b';
const line = '#e2e8f0';
const lightBg = '#f8fafc';

const Reveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Page = styled.div`
  background: #fff;
  color: ${ink};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 600px) { padding: 0 16px; }
`;

// ---------- Top partner bar ----------
const PartnerStrip = styled.div`
  background: #fff;
  border-bottom: 1px solid ${line};
  padding: 16px 0;

  .row {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }
  img { height: 42px; object-fit: contain; }
  .divider { width: 1px; height: 36px; background: ${line}; }
  .partner-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }
  .partner-text .name { font-weight: 700; color: ${navy}; font-size: 0.95rem; }
  .partner-text .sub { color: ${muted}; font-size: 0.7rem; }

  .tag {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${navy};
    font-weight: 700;
    font-size: 0.85rem;
    .accent { color: ${orange}; text-decoration: underline; text-decoration-color: ${orange}; text-underline-offset: 4px; }
    @media (max-width: 700px) { margin-left: 0; }
  }
`;

// ---------- Hero ----------
const Hero = styled.section`
  position: relative;
  background:
    radial-gradient(ellipse 50% 40% at 90% 20%, rgba(249, 115, 22, 0.1), transparent 60%),
    radial-gradient(ellipse 60% 50% at 10% 100%, rgba(10, 44, 110, 0.05), transparent 60%),
    #fff;
  padding: 60px 0 80px;
  overflow: hidden;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Kicker = styled.div`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  font-weight: 800;
  letter-spacing: 1px;
  color: ${navy};
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  .dot { color: ${orange}; }
  .rocket { font-size: 1.2em; }
`;

const BigTitle = styled.h1`
  font-size: clamp(3rem, 9vw, 7rem);
  font-weight: 900;
  line-height: 0.92;
  letter-spacing: -3px;
  font-style: italic;
  color: ${navy};
  margin: 0 0 8px;
  text-transform: uppercase;

  .stack { display: block; }
`;

const YearTag = styled.div`
  display: inline-block;
  background: ${navy};
  color: #fff;
  padding: 10px 28px;
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 900;
  letter-spacing: 3px;
  font-style: italic;
  margin: 12px 0;
  clip-path: polygon(4% 0, 100% 0, 96% 100%, 0% 100%);
`;

const Subhead = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${navy};
  margin-left: 8px;
  display: inline-block;
  vertical-align: middle;
`;

const Tagline = styled.p`
  font-size: 1.1rem;
  line-height: 1.55;
  color: ${ink};
  margin: 24px 0 0;
  max-width: 520px;
  font-style: italic;

  .highlight { color: ${navy}; font-weight: 800; font-style: normal; }
`;

const HeroVisual = styled.div`
  position: relative;
  aspect-ratio: 1/1;
  max-height: 520px;
  border-radius: 24px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(10, 44, 110, 0.85), rgba(249, 115, 22, 0.6)),
    url('/images/gallery/Robotics.jpg');
  background-size: cover;
  background-position: center;
  box-shadow: 0 24px 60px rgba(10, 44, 110, 0.18);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1), transparent 50%);
  }

  .badge {
    position: absolute;
    background: #fff;
    border-radius: 999px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.18);
    font-weight: 700;
    font-size: 0.8rem;
    z-index: 2;

    svg { color: ${orange}; }
  }
  .b1 { top: 18px; left: 18px; }
  .b2 { top: 18px; right: 18px; background: ${navy}; color: #fff; svg { color: ${orange}; } }
  .b3 { bottom: 18px; left: 18px; background: ${orange}; color: #fff; svg { color: #fff; } }
`;

// ---------- Highlights bar ----------
const Highlights = styled.div`
  background: ${navy};
  border-radius: 18px;
  padding: 24px 28px;
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const HItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;

  .icon-box {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: ${orange};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }
  .label { font-size: 0.88rem; font-weight: 700; line-height: 1.2; }
`;

// ---------- Section ----------
const Section = styled.section<{ alt?: boolean }>`
  padding: 80px 0;
  ${p => p.alt && css`background: ${lightBg};`}
  @media (max-width: 700px) { padding: 56px 0; }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 900;
  text-align: center;
  letter-spacing: -1px;
  margin: 0 0 8px;
  color: ${navy};
  text-transform: uppercase;

  .accent { color: ${orange}; }
`;

const SectionLead = styled.p`
  text-align: center;
  color: ${muted};
  font-size: 1.05rem;
  max-width: 600px;
  margin: 0 auto 48px;
  line-height: 1.6;
`;

// ---------- Explore cards ----------
const ExploreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const ExploreCard = styled(motion.div)`
  background: #fff;
  border: 1px solid ${line};
  border-radius: 18px;
  padding: 28px 22px;
  text-align: center;
  transition: all 0.3s;
  cursor: default;

  &:hover {
    transform: translateY(-6px);
    border-color: ${orange};
    box-shadow: 0 20px 50px rgba(10, 44, 110, 0.1);
  }

  .icon-wrap {
    width: 72px;
    height: 72px;
    margin: 0 auto 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${navy}, ${orangeDark});
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
  }
  h3 {
    font-size: 1.05rem;
    font-weight: 800;
    color: ${navy};
    margin: 0 0 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  p {
    color: ${muted};
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.5;
  }
`;

// ---------- Fee panel ----------
const FeeWrap = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 32px;
  align-items: center;
  background: linear-gradient(135deg, ${navy} 0%, ${navyDeep} 100%);
  border-radius: 24px;
  padding: 48px;
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    background: ${orange};
    opacity: 0.18;
    border-radius: 50%;
    top: -180px;
    right: -120px;
    filter: blur(60px);
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    padding: 32px 24px;
    text-align: center;
  }
`;

const FeeText = styled.div`
  position: relative;
  z-index: 1;

  h3 {
    color: #fff;
    font-size: clamp(1.4rem, 3vw, 2rem);
    font-weight: 900;
    margin: 0 0 12px;
    line-height: 1.2;
    letter-spacing: -0.5px;
  }
  p { color: rgba(255, 255, 255, 0.75); font-size: 1rem; line-height: 1.55; margin: 0; }
`;

const FeeAmount = styled.div`
  background: #fff;
  border: 2px solid ${orange};
  border-radius: 18px;
  padding: 28px 24px;
  text-align: center;
  position: relative;
  z-index: 1;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25);

  .label {
    display: inline-block;
    background: ${orange};
    color: #fff;
    padding: 4px 14px;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 1.5px;
    margin-bottom: 12px;
  }
  .price {
    color: ${orange};
    font-size: clamp(2.8rem, 5vw, 3.8rem);
    font-weight: 900;
    letter-spacing: -2px;
    line-height: 1;
  }
  .note { color: ${muted}; font-size: 0.85rem; margin-top: 8px; }
  .cta {
    margin-top: 18px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: ${navy};
    color: #fff;
    padding: 12px 24px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 700;
    font-size: 0.95rem;
    transition: transform 0.2s, background 0.2s;
    &:hover { transform: translateY(-2px); background: ${orangeDark}; }
  }
`;

// ---------- Logistics ----------
const LogiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  background: ${navy};
  border-radius: 20px;
  padding: 28px;
  color: #fff;

  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const LogiItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  .icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: ${orange};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  .label { font-size: 0.72rem; opacity: 0.7; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 700; }
  .value { font-size: 0.92rem; font-weight: 800; line-height: 1.3; }
`;

// ---------- Final CTA ----------
const FinalCTA = styled.div`
  background:
    radial-gradient(ellipse at 50% 0%, rgba(249, 115, 22, 0.18), transparent 60%),
    ${navy};
  border-radius: 24px;
  padding: 60px 40px;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;

  h2 {
    color: #fff;
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    margin: 0 0 8px;

    .orange { color: ${orange}; }
  }
  p { color: rgba(255, 255, 255, 0.75); margin: 0 0 28px; font-size: 1.05rem; }

  @media (max-width: 700px) { padding: 48px 24px; }
`;

const PrimaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${orange};
  color: #fff;
  padding: 16px 36px;
  border-radius: 12px;
  font-weight: 800;
  text-decoration: none;
  font-size: 1.05rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  box-shadow: 0 12px 32px rgba(249, 115, 22, 0.4);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    background: ${orangeDark};
    box-shadow: 0 16px 40px rgba(249, 115, 22, 0.5);
  }
`;

const ContactStrip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
  margin-top: 36px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);

  .item { display: flex; align-items: center; gap: 10px; font-size: 0.92rem; color: rgba(255, 255, 255, 0.92); }
  .item svg { color: ${orange}; }
  .item a { color: inherit; text-decoration: none; }
  .socials { display: flex; gap: 10px; }
  .sb {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    text-decoration: none;
    &:hover { background: ${orange}; border-color: ${orange}; transform: translateY(-2px); }
  }
`;

const MobileBar = styled.div`
  display: none;
  position: fixed;
  bottom: 0; left: 0; right: 0;
  background: #fff;
  border-top: 1px solid ${line};
  padding: 12px 16px;
  z-index: 100;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -8px 24px rgba(0,0,0,0.08);

  .price { color: ${ink}; font-weight: 800; font-size: 1.1rem; }
  .price small { color: ${muted}; font-weight: 500; font-size: 0.75rem; display: block; }
  a {
    background: ${orange};
    color: #fff;
    padding: 12px 22px;
    border-radius: 10px;
    font-weight: 700;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  @media (max-width: 700px) { display: flex; }
`;

const Spacer = styled.div`
  display: none;
  height: 70px;
  @media (max-width: 700px) { display: block; }
`;

// =================== Component ===================

const GreaterNoidaCamp: React.FC = () => {
  return (
    <Page>
      <PartnerStrip>
        <Container>
          <div className="row">
            <img src="/images/logo.png" alt="Dream Advance" />
            <div className="divider" />
            <div className="partner-text">
              <span className="name">IIITD Innovation &amp; Incubation Center</span>
              <span className="sub">Shaping Ideas to Reality</span>
            </div>
            <div className="tag">
              Future Builders Start <span className="accent">Here!</span>
            </div>
          </div>
        </Container>
      </PartnerStrip>

      <Hero>
        <Container>
          <HeroGrid>
            <Reveal>
              <div>
                <Kicker>
                  BUILD. <span className="dot">·</span> CREATE. <span className="dot">·</span> FLY. <span className="rocket"><FaRocket /></span>
                </Kicker>
                <BigTitle>
                  <span className="stack">Summer</span>
                  <span className="stack">Camp</span>
                </BigTitle>
                <YearTag>2K26</YearTag>
                <Subhead>for Young Innovators</Subhead>
                <Tagline>
                  Where students learn <span className="highlight">Robotics, AI &amp; Drone Tech</span> through hands-on activities.
                </Tagline>

                <Highlights>
                  <HItem>
                    <div className="icon-box"><FaRobot /></div>
                    <div className="label">Robotics<br />+ AI + IoT</div>
                  </HItem>
                  <HItem>
                    <div className="icon-box"><FaDrone /></div>
                    <div className="label">Drone Tech<br />+ Aero</div>
                  </HItem>
                  <HItem>
                    <div className="icon-box"><FaTrophy /></div>
                    <div className="label">Competitions<br />&amp; Fun</div>
                  </HItem>
                  <HItem>
                    <div className="icon-box"><FaBrain /></div>
                    <div className="label">Memory<br />Boosters</div>
                  </HItem>
                  <HItem>
                    <div className="icon-box"><FaGamepad /></div>
                    <div className="label">Interactive<br />Learning</div>
                  </HItem>
                </Highlights>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <HeroVisual>
                <span className="badge b1"><FaMicrochip /> AI</span>
                <span className="badge b2"><FaDrone /> Drone</span>
                <span className="badge b3"><FaBullseye /> Hands-on</span>
              </HeroVisual>
            </Reveal>
          </HeroGrid>
        </Container>
      </Hero>

      <Section alt>
        <Container>
          <Reveal>
            <SectionTitle>What Your Child Will <span className="accent">Explore</span></SectionTitle>
            <SectionLead>
              Four hands-on streams. One unforgettable summer.
            </SectionLead>
          </Reveal>
          <ExploreGrid>
            <Reveal delay={0.05}>
              <ExploreCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaRobot /></div>
                <h3>Robotics</h3>
                <p>Build. Code. Innovate.</p>
              </ExploreCard>
            </Reveal>
            <Reveal delay={0.12}>
              <ExploreCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaBrain /></div>
                <h3>Artificial Intelligence</h3>
                <p>Explore. Train. Create.</p>
              </ExploreCard>
            </Reveal>
            <Reveal delay={0.19}>
              <ExploreCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaDrone /></div>
                <h3>Drone Tech</h3>
                <p>Fly. Learn. Experiment.</p>
              </ExploreCard>
            </Reveal>
            <Reveal delay={0.26}>
              <ExploreCard whileHover={{ y: -6 }}>
                <div className="icon-wrap"><FaPlane /></div>
                <h3>Aero Modelling</h3>
                <p>Design. Build. Soar High.</p>
              </ExploreCard>
            </Reveal>
          </ExploreGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <Reveal>
            <FeeWrap>
              <FeeText>
                <h3>Everything Included.<br />No Hidden Charges.</h3>
                <p>Kits, materials, expert mentorship, certificate, and a final-day showcase — all wrapped into one final fee. Limited seats, register early.</p>
              </FeeText>
              <FeeAmount>
                <span className="label">FINAL FEE</span>
                <div className="price">₹4,000<small style={{ fontSize: '0.5em' }}>/-</small></div>
                <div className="note">All inclusive</div>
                <Link to="/register-noida" className="cta">
                  Register Now <FaArrowRight />
                </Link>
              </FeeAmount>
            </FeeWrap>
          </Reveal>
        </Container>
      </Section>

      <Section alt>
        <Container>
          <Reveal>
            <SectionTitle>Camp <span className="accent">Details</span></SectionTitle>
            <SectionLead>Everything you need to know in one strip.</SectionLead>
          </Reveal>
          <Reveal delay={0.1}>
            <LogiGrid>
              <LogiItem>
                <div className="icon"><FaMapMarkerAlt /></div>
                <div>
                  <div className="label">Venue</div>
                  <div className="value">SDS Residency<br />Greater Noida</div>
                </div>
              </LogiItem>
              <LogiItem>
                <div className="icon"><FaCalendarAlt /></div>
                <div>
                  <div className="label">Duration</div>
                  <div className="value">10 Days Camp</div>
                </div>
              </LogiItem>
              <LogiItem>
                <div className="icon"><FaClock /></div>
                <div>
                  <div className="label">Daily</div>
                  <div className="value">9 AM – 12 PM</div>
                </div>
              </LogiItem>
              <LogiItem>
                <div className="icon"><FaUsers /></div>
                <div>
                  <div className="label">Age Group</div>
                  <div className="value">10 – 18 Years</div>
                </div>
              </LogiItem>
            </LogiGrid>
          </Reveal>
        </Container>
      </Section>

      <Section>
        <Container>
          <Reveal>
            <FinalCTA>
              <span style={{ display: 'inline-block', background: orange, color: '#fff', padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 800, letterSpacing: 1.5, marginBottom: 16 }}>
                LIMITED SEATS!
              </span>
              <h2>Register <span className="orange">Now</span></h2>
              <p>Reserve a seat at the Greater Noida edition. Future Builders Start Here.</p>
              <PrimaryBtn to="/register-noida">
                Register Now <FaArrowRight />
              </PrimaryBtn>

              <ContactStrip>
                <div className="item">
                  <FaPhone />
                  <a href="tel:+917380612016">+91 73806 12016</a>
                </div>
                <div className="item">
                  <FaEnvelope />
                  <a href="mailto:connect@cograd.in">connect@cograd.in</a>
                </div>
                <div className="socials">
                  <a className="sb" href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>
                  <a className="sb" href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
                  <a className="sb" href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebook /></a>
                </div>
              </ContactStrip>
            </FinalCTA>
          </Reveal>
        </Container>
      </Section>

      <Spacer />
      <MobileBar>
        <div className="price">
          ₹4,000
          <small>Final fee · all-inclusive</small>
        </div>
        <Link to="/register-noida">
          Register <FaArrowRight />
        </Link>
      </MobileBar>
    </Page>
  );
};

export default GreaterNoidaCamp;
